import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { Call } from 'features/call/call-base/interfaces';
import { hearingInfoReceived } from 'features/call/call-base/store/callEvents';
import {
  adaptIncomingCallDeaf,
  isSorensonRegistrationType,
} from 'features/call/call-deaf/helpers';
import type {
  CallDeafState,
  DeafParticipantStatus,
} from 'features/call/call-deaf/interfaces';
import type { DeafPartyResolutionResult } from 'features/call/call-accepted/interfaces';

export const initialCallDeafState: CallDeafState = {
  name: '',
  phoneNumber: '',
  location: '',
  status: 'disconnected',
  isLoading: false,
  isMuted: false,
  connectionAttempts: 0,
  isSorenson: false,
  hasConnected: false,
};

export const callDeafSlice = createSlice({
  name: 'call-deaf',
  initialState: initialCallDeafState,
  reducers: {
    setReceivedDeaf: (state, action: PayloadAction<Call>) => {
      return {
        ...state,
        ...adaptIncomingCallDeaf(action.payload),
        isSorenson: isSorensonRegistrationType(action.payload.RegistrationType),
      };
    },
    // TODO: refactor the rest of these setters as part of ARC-7598.
    setDeafStatus: (state, action: PayloadAction<DeafParticipantStatus>) => {
      const isConnecting = action.payload === 'connecting';
      if (isConnecting) {
        state.connectionAttempts += 1;
      }
      state.status = action.payload;
    },
    deafConnected: (state) => {
      state.hasConnected = true;
      state.status = 'connected';
    },
    setDeafPhoneNumber: (state, action: PayloadAction<string>) => {
      state.phoneNumber = action.payload.replaceAll('+', '');
    },
    setDeafLocation: (state, action: PayloadAction<string>) => {
      state.location = action.payload;
    },
    setDeafIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    muteDeafAudio: (state) => {
      state.isMuted = true;
    },
    unmuteDeafAudio: (state) => {
      state.isMuted = false;
    },
    resetDeafAudio: (state) => {
      state.isMuted = false;
    },
    deafPartyResolved: (
      state,
      action: PayloadAction<PartialResolutionResult>
    ) => {
      state.name = action.payload.name;
      state.isSorenson = action.payload.isSorenson;
    },
    resetCallDeaf: () => initialCallDeafState,
  },
  extraReducers: (builder) => {
    builder.addCase(hearingInfoReceived, (state, action) => {
      const calleeConnectionLocation =
        action.payload.Call?.CalleeConnectionLocation;

      if (calleeConnectionLocation != null) {
        state.location = calleeConnectionLocation;
      }
    });
  },
});

export const {
  setReceivedDeaf,
  setDeafStatus,
  deafConnected,
  setDeafPhoneNumber,
  setDeafLocation,
  setDeafIsLoading,
  muteDeafAudio,
  unmuteDeafAudio,
  resetDeafAudio,
  resetCallDeaf,
  deafPartyResolved,
} = callDeafSlice.actions;

export const callDeafReducer = callDeafSlice.reducer;

// Helper type to make a code change easier.
// Original two properties are required, the rest are optional.
type PartialResolutionResult = Partial<
  Omit<DeafPartyResolutionResult, 'name' | 'isSorenson'>
> &
  Required<Pick<DeafPartyResolutionResult, 'name' | 'isSorenson'>>;
